import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../hooks/useAuth';
import { Plus, Search, Building2, ChevronRight, Tag } from 'lucide-react';

const TAG_STYLES = {
  'active':     'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'pending':    'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'slow':       'bg-red-500/15 text-red-400 border-red-500/30',
  'high-value': 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  'hot':        'bg-orange-500/15 text-orange-400 border-orange-500/30',
};

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ company_name: '', industry: '', website: '', notes: '', tags: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (tagFilter) params.set('tag', tagFilter);
      const { data } = await api.get(`/clients?${params}`);
      setClients(data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, tagFilter]);

  const saveClient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/clients', form);
      setShowModal(false);
      setForm({ company_name: '', industry: '', website: '', notes: '', tags: [] });
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tag) => {
    setForm(f => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag]
    }));
  };

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Clients</h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage your client relationships</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={15} /> Add Client
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search clients..."
            className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>
        {['active','pending','slow','high-value','hot'].map(tag => (
          <button
            key={tag}
            onClick={() => setTagFilter(tagFilter === tag ? '' : tag)}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition-colors capitalize ${
              tagFilter === tag
                ? TAG_STYLES[tag] || 'bg-blue-600 text-white border-blue-500'
                : 'border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {clients.map(client => (
            <div
              key={client.id}
              onClick={() => navigate(`/clients/${client.id}`)}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-5 cursor-pointer hover:border-slate-700 hover:bg-slate-800/50 transition-all group"
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <Building2 size={18} className="text-slate-400" />
                </div>
                <ChevronRight size={16} className="text-slate-600 group-hover:text-slate-400 transition-colors mt-1" />
              </div>
              <div className="mt-3">
                <div className="font-semibold text-white">{client.company_name}</div>
                {client.industry && <div className="text-xs text-slate-500 mt-0.5">{client.industry}</div>}
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {(client.tags || []).map(tag => (
                  <span key={tag} className={`text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${TAG_STYLES[tag] || 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-800 text-xs text-slate-500">
                <span>{client.document_count || 0} docs</span>
                <span>{client.contact_count || 0} contacts</span>
                {client.assigned_to_name && <span>→ {client.assigned_to_name}</span>}
              </div>
            </div>
          ))}

          {clients.length === 0 && (
            <div className="col-span-3 text-center py-16 text-slate-500">
              <Building2 size={32} className="mx-auto mb-3 opacity-30" />
              <p>No clients found</p>
              <button onClick={() => setShowModal(true)} className="mt-3 text-blue-400 hover:text-blue-300 text-sm">Add your first client</button>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-lg font-bold text-white mb-5">Add Client</h2>
            <form onSubmit={saveClient} className="space-y-4">
              <input
                required
                placeholder="Company Name *"
                value={form.company_name}
                onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
              <input
                placeholder="Industry"
                value={form.industry}
                onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
              <input
                placeholder="Website"
                value={form.website}
                onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
              <textarea
                placeholder="Notes..."
                rows={2}
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
              />
              <div>
                <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider font-semibold">Tags</div>
                <div className="flex flex-wrap gap-2">
                  {['active','pending','slow','high-value','hot'].map(tag => (
                    <button
                      type="button"
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`text-xs px-3 py-1.5 rounded-full font-semibold border capitalize transition-colors ${
                        form.tags.includes(tag)
                          ? TAG_STYLES[tag] || 'bg-blue-600 text-white border-blue-500'
                          : 'border-slate-700 text-slate-400'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-slate-700 text-slate-400 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors">
                  {saving ? 'Saving...' : 'Save Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
